class Dropdown {
    private static instances: Dropdown[] = [];
    private static readonly ANIMATION_DURATION_MS = 180;
    private static readonly ANIMATION_STAGGER_MS = 50;
    private static readonly ANIMATION_OFFSET_Y_PX = 8;
    private static readonly HOVER_CLOSE_DELAY_MS = 120;
    private dropdown: HTMLElement;
    private toggle!: HTMLElement;
    private content!: HTMLElement;
    private icon: SVGElement | null = null;
    private header: HTMLElement | null = null;
    private isOpen: boolean = false;
    private closeTimeoutId: number | null = null;

    constructor(element: HTMLElement) {
        this.dropdown = element;

        // Get references to toggle and next sibling content
        const toggle = this.dropdown.querySelector('.dropdown-toggle');
        const content = this.dropdown.nextElementSibling;

        if (!toggle || !content || !content.classList.contains('dropdown-content')) {
            // console.error('Dropdown elements not found');
            return;
        }

        this.toggle = toggle as HTMLElement;
        this.content = content as HTMLElement;
        this.icon = this.toggle.querySelector('.dropdown-icon');
        this.header = document.querySelector('header');

        // Add this instance to the static array
        Dropdown.instances.push(this);

        this.init();
    }

    private init() {
        // Open on hover/focus for desktop navigation
        this.dropdown.addEventListener('mouseenter', () => {
            this.openDropdown();
        });

        this.dropdown.addEventListener('mouseleave', (e) => {
            const to = e.relatedTarget as Node | null;
            if (to && this.content.contains(to)) return; // moving into the content panel
            this.scheduleClose();
        });

        // Keep open while hovering over the dropdown content (sibling element)
        this.content.addEventListener('mouseenter', () => {
            if (this.closeTimeoutId !== null) {
                window.clearTimeout(this.closeTimeoutId);
                this.closeTimeoutId = null;
            }
        });

        this.content.addEventListener('mouseleave', (e) => {
            const to = e.relatedTarget as Node | null;
            if (to && this.dropdown.contains(to)) return; // moving back into the toggle
            this.scheduleClose();
        });

        this.toggle.addEventListener('focus', () => {
            this.openDropdown();
        });

        this.dropdown.addEventListener('focusout', (e) => {
            const nextFocused = e.relatedTarget as Node | null;
            if (
                !nextFocused ||
                (!this.dropdown.contains(nextFocused) && !this.content.contains(nextFocused))
            ) {
                this.scheduleClose();
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const target = e.target as Node;
            const mobileMenu = document.getElementById('mobilemenu');

            // Don't close if clicking on mobile menu
            if (mobileMenu && mobileMenu.contains(target)) {
                return;
            }

            if (!this.dropdown.contains(target) && !this.content.contains(target)) {
                this.closeDropdown();
            }
        });

        // Close dropdown when pressing Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeDropdown();
            }
        });
    }

    private toggleDropdown() {
        if (this.isOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    private scheduleClose() {
        if (this.closeTimeoutId !== null) {
            window.clearTimeout(this.closeTimeoutId);
        }

        this.closeTimeoutId = window.setTimeout(() => {
            this.closeDropdown();
        }, Dropdown.HOVER_CLOSE_DELAY_MS);
    }

    private openDropdown() {
        // Close all other dropdowns
        Dropdown.instances.forEach((instance) => {
            if (instance !== this && instance.isOpen) {
                instance.closeDropdown();
            }
        });

        if (this.closeTimeoutId !== null) {
            window.clearTimeout(this.closeTimeoutId);
            this.closeTimeoutId = null;
        }

        this.isOpen = true;
        this.content.classList.remove('hidden');
        this.toggle.setAttribute('aria-expanded', 'true');
        this.animateSubmenuItems(true);

        // Expand header to full height
        if (this.header) {
            this.header.classList.add('h-screen');
            this.header.classList.remove('h-auto');
        }

        // Rotate icon
        if (this.icon) {
            this.icon.style.transform = 'rotate(180deg)';
        }
    }

    private closeDropdown() {
        this.isOpen = false;
        this.toggle.setAttribute('aria-expanded', 'false');

        if (this.closeTimeoutId !== null) {
            window.clearTimeout(this.closeTimeoutId);
            this.closeTimeoutId = null;
        }

        const closeAnimationTime = this.animateSubmenuItems(false);
        this.closeTimeoutId = window.setTimeout(() => {
            if (!this.isOpen) {
                this.content.classList.add('hidden');
            }
            this.closeTimeoutId = null;
        }, closeAnimationTime);

        // Restore header to normal height
        if (this.header) {
            this.header.classList.remove('h-screen');
            this.header.classList.add('h-auto');
        }

        // Reset icon rotation
        if (this.icon) {
            this.icon.style.transform = 'rotate(0deg)';
        }
    }

    private animateSubmenuItems(isOpening: boolean): number {
        const items = Array.from(this.content.querySelectorAll<HTMLElement>('.dropdown-submenu > *'));
        if (items.length === 0) {
            return 0;
        }

        const duration = Dropdown.ANIMATION_DURATION_MS;
        const stagger = Dropdown.ANIMATION_STAGGER_MS;

        items.forEach((item, index) => {
            const sequenceIndex = isOpening ? index : items.length - 1 - index;
            item.style.transitionProperty = 'opacity, transform';
            item.style.transitionDuration = `${duration}ms`;
            item.style.transitionTimingFunction = 'ease';
            item.style.transitionDelay = `${sequenceIndex * stagger}ms`;

            if (isOpening) {
                item.style.opacity = '0';
                item.style.transform = `translateY(-${Dropdown.ANIMATION_OFFSET_Y_PX}px)`;
            }
        });

        if (isOpening) {
            requestAnimationFrame(() => {
                items.forEach((item) => {
                    item.style.opacity = '1';
                    item.style.transform = 'translateY(0)';
                });
            });
        } else {
            items.forEach((item) => {
                item.style.opacity = '0';
                item.style.transform = `translateY(-${Dropdown.ANIMATION_OFFSET_Y_PX}px)`;
            });
        }

        return duration + (items.length - 1) * stagger;
    }

    public destroy() {
        // Remove this instance from the static array
        const index = Dropdown.instances.indexOf(this);
        if (index > -1) {
            Dropdown.instances.splice(index, 1);
        }

        // Clean up event listeners if needed
        this.toggle.removeEventListener('click', this.toggleDropdown);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const dropdowns = document.querySelectorAll(".dropdown") as NodeListOf<HTMLElement>;
    dropdowns.forEach((dropdown) => {
        new Dropdown(dropdown);
    });
});

export default Dropdown;
